/** Shared formatting helpers + trust badges for the OMS operator UI (spec §8).
 *
 *  Keeps the "protected" critical-count contract in ONE place: a suppressed
 *  count (`critical_customers_protected === true`, value `null`) is NEVER
 *  rendered as a number — see {@link CriticalCountBadge}. */

import type {
  CauseCode,
  OutageListItem,
  OutagePriority,
  OutageScope,
  OutageStatus,
} from '../../types/oms.ts'
import {
  CAUSE_CODE_LABELS,
  OUTAGE_SCOPE_COLORS,
  PRIORITY_CLASSES,
  PRIORITY_LABELS,
} from '../../types/oms.ts'

// =========================================================================
// Time / duration formatting
// =========================================================================

/** Local wall-clock "HH:MM" (24h) for the freshness banner + timeline. */
export function formatClock(iso: string | null | undefined): string {
  if (!iso) return '--:--'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '--:--'
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/** Local date + time, compact. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '--'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '--'
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** "2h 14m" style elapsed from a duration in seconds. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) return '--'
  const total = Math.floor(seconds)
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const mins = Math.floor((total % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

/** Relative "3m ago" / "1h ago" for last-signal freshness. */
export function formatAgo(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'never'
  const secs = Math.round((Date.now() - d.getTime()) / 1000)
  if (secs < 60) return `${secs}s ago`
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/** ISO instant -> the `YYYY-MM-DDTHH:mm` value an <input type="datetime-local">
 *  expects, in LOCAL time. Empty string for null/invalid (an unset ETR). */
export function toDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

// =========================================================================
// Labels
// =========================================================================

const SCOPE_LABELS: Record<OutageScope, string> = {
  substation: 'Substation',
  feeder: 'Feeder',
  device: 'Protective device',
  transformer: 'Transformer',
  meter: 'Meter',
}

const STATUS_LABELS: Record<OutageStatus, string> = {
  active: 'Active',
  restored: 'Restored',
  closed: 'Closed',
  cancelled: 'Cancelled',
}

const STATUS_CLASSES: Record<OutageStatus, string> = {
  active: 'bg-red-100 text-red-800',
  restored: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-gray-100 text-gray-500 line-through',
}

const SOURCE_LABELS: Record<string, string> = {
  lg_sftp: 'L+G feed',
  ami_inferred: 'AMI inferred',
  manual: 'Manual',
  aggregated: 'Aggregated',
}

export function scopeLabel(scope: OutageScope): string {
  return SCOPE_LABELS[scope] ?? scope
}

export function causeLabel(code: string | null | undefined): string {
  if (!code) return '--'
  return CAUSE_CODE_LABELS[code as CauseCode] ?? code
}

/** The cause to show: the PowerTRX cause when one was picked, else the coarse
 *  code's label. */
export function causeText(item: {
  cause_code: string | null
  cause_detail?: string | null
}): string {
  return item.cause_detail || causeLabel(item.cause_code)
}

/** "transformer #42" device identity, tolerant of an UNRESOLVED meter-scope
 *  event (`device_id === null` — the raw feed meter number had no GIS
 *  crosswalk; identity is then `unresolved_meter_number`). The plain-language
 *  suffix replaces the old unresolved-jargon label (UX B2).
 *
 *  A `device`-scope event (a protective-device parent) names the device the way
 *  a lineman would when its `suggestion_detail` is at hand: "fuse N9373 ·
 *  phase A" (type + label + out phases), degrading to "device N9373" and then
 *  to the plain "device #1640" as the detail thins out. */
export function deviceLabel(
  scope: OutageScope,
  deviceId: number | null | undefined,
  unresolvedMeterNumber?: string | null,
  suggestionDetail?: Record<string, unknown> | null,
): string {
  if (scope === 'device' && suggestionDetail) {
    const label =
      typeof suggestionDetail.device_label === 'string'
        ? suggestionDetail.device_label
        : null
    if (label) {
      const kind =
        typeof suggestionDetail.device_type === 'string' && suggestionDetail.device_type
          ? suggestionDetail.device_type
          : 'device'
      const phases =
        typeof suggestionDetail.out_phases === 'string' && suggestionDetail.out_phases
          ? suggestionDetail.out_phases
          : null
      const phaseSuffix = phases
        ? ` · phase${phases.length > 1 ? 's' : ''} ${phases}`
        : ''
      return `${kind} ${label}${phaseSuffix}`
    }
  }
  if (deviceId !== null && deviceId !== undefined) return `${scope} #${deviceId}`
  if (unresolvedMeterNumber) return `meter ${unresolvedMeterNumber} (not in system model)`
  return `${scope} (not in system model)`
}

// =========================================================================
// Badges
// =========================================================================

/** Colored dot keyed to the scope palette (Red feeder/substation, Orange
 *  transformer, Yellow meter). */
export function ScopeBadge({ scope }: { scope: OutageScope }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
      <span
        className="w-2.5 h-2.5 rounded-full shrink-0"
        style={{ backgroundColor: OUTAGE_SCOPE_COLORS[scope] }}
      />
      {scopeLabel(scope)}
    </span>
  )
}

export function StatusBadge({ status }: { status: OutageStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        STATUS_CLASSES[status] ?? 'bg-gray-100 text-gray-700'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  )
}

/** Trust badge keyed to the detection source (spec §8 trust signals):
 *  - operator-confirmed (`confirmed_at`) → "Confirmed"
 *  - `lg_sftp` → "Last-gasp": a HARD L+G feed signal is reportable on its own
 *    and must NEVER read as Inferred
 *  - `manual` → "Manual" (dispatcher-created, unreportable until confirmed)
 *  - `ami_inferred` (and any unknown source) unconfirmed → "Inferred" */
export function TrustBadge({ item }: { item: OutageListItem }) {
  const confirmed = item.confirmed_at !== null
  const sourceLabel = SOURCE_LABELS[item.detection_source] ?? item.detection_source
  if (confirmed) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800"
        title={`Confirmed${item.confirmed_by ? ` by ${item.confirmed_by}` : ''} · ${sourceLabel}`}
      >
        Confirmed
      </span>
    )
  }
  if (item.detection_source === 'lg_sftp') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-emerald-600 text-white"
        title="Hard last-gasp signal from the L+G feed · reportable"
      >
        Last-gasp
      </span>
    )
  }
  if (item.detection_source === 'manual') {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
        title="Manually created · unreportable until confirmed"
      >
        Manual
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800"
      title={`Inferred / unconfirmed · source: ${sourceLabel}`}
    >
      Inferred
    </span>
  )
}

/** Detection-source pill + confidence. */
export function SourceBadge({ item }: { item: OutageListItem }) {
  const label = SOURCE_LABELS[item.detection_source] ?? item.detection_source
  const pct =
    item.confidence !== null ? ` · ${Math.round(item.confidence * 100)}%` : ''
  return (
    <span className="text-xs text-gray-600" title="Detection source and rolled-up confidence">
      {label}
      {pct}
    </span>
  )
}

/** Priority pill keyed to the enum (spec §4.4). An unknown/legacy string renders
 *  as a neutral gray pill rather than being dropped. */
export function PriorityBadge({ priority }: { priority: string | null }) {
  if (!priority) return <span className="text-xs text-gray-400">--</span>
  const known = (priority as OutagePriority) in PRIORITY_LABELS
  const cls = known
    ? PRIORITY_CLASSES[priority as OutagePriority]
    : 'bg-gray-100 text-gray-600'
  const label = known ? PRIORITY_LABELS[priority as OutagePriority] : priority
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {label}
    </span>
  )
}

/** Banner copy for a source-device suggestion (spec §4.2): "covers 3/3 out
 *  transformers, 92% confidence". Returns null when the detail is absent. */
export function formatSuggestion(
  detail: Record<string, unknown> | null,
  confidence: number | null,
): { label: string; coverage: string; confidence: string } | null {
  if (!detail) return null
  const covered = typeof detail.covered === 'number' ? detail.covered : null
  const out =
    typeof detail.out_transformers === 'number' ? detail.out_transformers : null
  const label =
    typeof detail.device_label === 'string' ? detail.device_label : 'device'
  const coverage =
    covered !== null && out !== null
      ? `covers ${covered}/${out} out transformer${out === 1 ? '' : 's'}`
      : 'coverage unknown'
  const confidenceStr =
    confidence !== null ? `${Math.round(confidence * 100)}% confidence` : ''
  return { label, coverage, confidence: confidenceStr }
}

/** The critical-customer count with the HARD "protected" contract: when the
 *  server suppressed the count (`protected === true`), render the shield word —
 *  NEVER a number. A real 0 (not protected) renders as "0". */
export function CriticalCountBadge({
  count,
  protectedFlag,
}: {
  count: number | null
  protectedFlag: boolean
}) {
  if (protectedFlag) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
        title="Critical-customer count is protected for your role"
      >
        Protected
      </span>
    )
  }
  if (count === null || count === 0) {
    return <span className="text-xs text-gray-400">0</span>
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
      title="Critical customers affected"
    >
      {count} critical
    </span>
  )
}
